import { PortableTextBlock } from "@portabletext/react"
import sanityQuery from "./sanity"
import {
  Update, UpdateBody, UpdateBodyDivider, UpdateImage,
} from "./types"
import { isUpdateTypeSlug } from "./utils"

export const UPDATES_LIMIT = 3

const LATEST = `*[_type == "update" && defined(slug.current) && defined(publishedAt) && publishedAt <= now()] | order(publishedAt desc)[0...${UPDATES_LIMIT}]`
const IMAGE = `{
  _key, alt, "src": asset->url,
  "width": asset->metadata.dimensions.width, "height": asset->metadata.dimensions.height
}`

type ImageResult = { _key?: string, alt?: string, src?: string, width?: number, height?: number }
type BodyImageResult = ImageResult & { _type: "image" }
type PostResult = {
  _id: string
  title: string
  slug: string
  publishedAt: string
  types?: string[]
  tags?: ({ slug?: string, name: string } | null)[]
  cover?: ImageResult | null
  body?: (PortableTextBlock | BodyImageResult | UpdateBodyDivider)[]
}

const IMAGE_WIDTHS = [480, 720, 1080, 1440, 2160]

const resolveImage = (src: string, { alt, width, height }: ImageResult): UpdateImage => {
  const widths = width ? [...IMAGE_WIDTHS.filter((w) => w < width), width] : []

  return {
    src: `${src}?auto=format`,
    alt: alt ?? "",
    width,
    height,
    srcSet: widths.length ? widths.map((w) => `${src}?auto=format&fit=max&w=${w} ${w}w`).join(", ") : undefined,
  }
}

const isImage = (
  block: PortableTextBlock | BodyImageResult | UpdateBodyDivider,
): block is BodyImageResult => {
  const { _type } = block

  return _type === "image"
}

const toPost = ({
  _id, title, slug, publishedAt, types, tags, cover, body,
}: PostResult): Update => ({
  id: _id,
  slug,
  title,
  publishedAt,
  types: (types ?? []).filter(isUpdateTypeSlug),
  tags: (tags ?? []).flatMap((tag) => (tag?.slug ? [{ slug: tag.slug, name: tag.name }] : [])),
  cover: cover?.src ? resolveImage(cover.src, cover) : null,
  body: (body ?? []).flatMap<UpdateBody[number]>((block) => {
    if (!isImage(block)) {
      return [block]
    }

    const { _key, src } = block

    return src ? [{ _type: "image" as const, _key: _key ?? "", ...resolveImage(src, block) }] : []
  }),
})

export const getUpdateDates = () => sanityQuery<{ _id: string, publishedAt: string }[]>(
  `${LATEST} { _id, publishedAt }`,
).then((posts) => posts.map(({ _id, publishedAt }) => ({ id: _id, publishedAt })))

export const getUpdates = () => sanityQuery<PostResult[]>(
  `${LATEST} {
    _id, title, "slug": slug.current, publishedAt, types,
    "tags": tags[]->{ "slug": slug.current, name },
    "cover": cover${IMAGE},
    "body": body[]{ ..., _type == "image" => ${IMAGE} }
  }`,
).then((posts) => posts.map(toPost))
